import React, { useState, useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import supabase from '../../config/supabaseClient';

const ProfileContainer = styled.div`
  position: relative;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary-light);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
`;

const UserInfo = styled.div`
  text-align: left;
`;

const UserName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  min-width: 200px;
  z-index: 1000;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  background: none;
  border: none;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background: var(--surface-hover);
  }

  ${props => props.danger && `
    color: var(--error);
  `}
`;

const Divider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 8px 0;
`;

const ProfileMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const menuRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getInitials = (name) => {
    return name
      ? name.split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
      : '👤';
  };

  return (
    <ProfileContainer ref={menuRef}>
      <ProfileButton onClick={() => setIsOpen(!isOpen)}>
        <Avatar>{getInitials(user?.user_metadata?.full_name)}</Avatar>
        <UserInfo>
          <UserName>{user?.user_metadata?.full_name || 'User'}</UserName>
          <UserEmail>{user?.email}</UserEmail>
        </UserInfo>
      </ProfileButton>

      {isOpen && (
        <DropdownMenu>
          
          <MenuItem danger onClick={handleLogout}>
            🚪 Sign Out
          </MenuItem>
        </DropdownMenu>
      )}
    </ProfileContainer>
  );
};

export default ProfileMenu;